import { lazy, Suspense, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Spinner } from '@heroui/react';
import { Highlighter } from 'lucide-react';
import { useKnowledgeItems, useKnowledgePages, usePageTileInfo } from '@/api/knowledge';
import { KnowledgeItemType } from '@estimator/shared';
import { useMarkups, useCreateMarkup } from '@/api/markups';
import { useMarkupStore } from '@/store/markupStore';
import ContextToolbar from './ContextToolbar';
import PageSelector from './PageSelector';
import ReviewPanel from './ReviewPanel';
import ZoomControls from './ZoomControls';
import DrawingToolbar from './DrawingToolbar';
import ScaleCalibration from './ScaleCalibration';
import ScalePillsBar from './ScalePillsBar';
import type Map from 'ol/Map';

const TileViewer = lazy(() => import('./TileViewer'));

interface MarkupViewerProps {
  projectId: string;
  initialDrawingId?: string;
}

export default function MarkupViewer({ projectId, initialDrawingId }: MarkupViewerProps) {
  const { isMobile, isTablet } = useBreakpoint();
  const [olMap, setOlMap] = useState<Map | null>(null);

  const {
    currentDrawingId,
    currentPageId,
    activeTool,
    activeContext,
    activeItemCategoryId,
    setCurrentDrawing,
    setCurrentPage,
    setActiveTool,
  } = useMarkupStore();

  // Fetch data
  const { data: knowledgeResponse, isLoading: drawingsLoading } = useKnowledgeItems(projectId, KnowledgeItemType.DRAWING);
  const knowledgeItems = knowledgeResponse?.data ?? [];
  const { data: markups = [], isLoading: markupsLoading } = useMarkups(projectId);
  const createMarkup = useCreateMarkup();

  // Tile info for scale calibration
  const { data: tileInfo } = usePageTileInfo(currentPageId ?? null);

  // Select initial drawing
  useEffect(() => {
    if (knowledgeItems.length === 0) return;
    const targetId = initialDrawingId ?? knowledgeItems[0].id;
    if (currentDrawingId !== targetId) {
      setCurrentDrawing(targetId);
    }
  }, [knowledgeItems, initialDrawingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch pages for the current drawing
  const { data: pages = [], isLoading: pagesLoading } = useKnowledgePages(currentDrawingId ?? '');

  // Page index state
  const [pageIndex, setPageIndex] = useState(0);

  // Set initial page when pages load
  useEffect(() => {
    if (pages.length === 0) return;
    const idx = currentPageId ? pages.findIndex((p) => p.id === currentPageId) : 0;
    const validIdx = idx >= 0 ? idx : 0;
    setPageIndex(validIdx);
    setCurrentPage(pages[validIdx].id);
  }, [pages]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = useCallback(
    (idx: number) => {
      if (idx >= 0 && idx < pages.length) {
        setPageIndex(idx);
        setCurrentPage(pages[idx].id);
      }
    },
    [pages, setCurrentPage],
  );

  const handleDrawingChange = useCallback(
    (drawingId: string) => {
      setCurrentDrawing(drawingId);
      setPageIndex(0);
    },
    [setCurrentDrawing],
  );

  // Filter markups for the current page
  const pageMarkups = useMemo(() => {
    if (!currentPageId) return markups;
    return markups.filter(
      (m) => m.page_id === currentPageId || m.page_id === null,
    );
  }, [markups, currentPageId]);

  // Handle markup creation from TileViewer
  const handleCreateMarkup = useCallback(
    (data: any) => {
      createMarkup.mutate({
        projectId,
        ...data,
        item_category_id: activeItemCategoryId ?? undefined,
        markup_context: activeContext,
      });
    },
    [projectId, createMarkup, activeItemCategoryId, activeContext],
  );

  // Keyboard shortcuts for tools
  useEffect(() => {
    const shortcuts: Record<string, typeof activeTool> = {
      v: 'select', r: 'area-rect', p: 'area-polygon', l: 'linear',
      k: 'polyline', a: 'angle', n: 'count', s: 'known-scale', c: 'calibrate',
    };
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const tool = shortcuts[e.key.toLowerCase()];
      if (tool) setActiveTool(tool);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setActiveTool]);

  const isLoading = drawingsLoading || markupsLoading || pagesLoading;

  // No drawings state
  if (!isLoading && knowledgeItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-3">
        <Highlighter size={48} style={{ color: 'var(--text-muted)' }} />
        <p className="text-base font-medium" style={{ color: 'var(--text-secondary)' }}>
          No drawings uploaded
        </p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Upload drawings first, then run AI markup extraction.
        </p>
      </div>
    );
  }

  return (
    <div
      className="flex"
      style={{
        /* Full viewport minus the Navbar (64px) and the project header/tabs (~120px) */
        height: 'calc(100vh - 64px - 120px)', flexDirection: isTablet ? 'column' : 'row',
      }}
    >
      {/* Left: Tile Viewer (65%) */}
      <div
        className="flex flex-col"
        style={{
          width: isTablet ? '100%' : '65%', height: isTablet ? '60%' : undefined, minWidth: 0, borderRight: isTablet ? undefined : '1px solid var(--border)', borderBottom: isTablet ? '1px solid var(--border)' : undefined,
        }}
      >
        {/* Page selector bar */}
        <PageSelector
          drawings={knowledgeItems}
          currentDrawingId={currentDrawingId}
          pages={pages}
          currentPageIndex={pageIndex}
          onDrawingChange={handleDrawingChange}
          onPageChange={handlePageChange}
        />

        {/* Scale quick-assign pills */}
        {currentPageId && tileInfo && (
          <ScalePillsBar
            pageId={currentPageId}
            currentScale={tileInfo.scalePixelsPerUnit}
            currentUnit={tileInfo.scaleUnit || 'ft'}
          />
        )}

        {/* Context + item type toolbox */}
        <ContextToolbar />

        {/* Viewer area — relative container for absolute-positioned overlays */}
        <div className="flex-1 relative" style={{ minHeight: 0 }}>
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-base)' }}>
              <div className="flex flex-col items-center gap-2">
                <Spinner size="lg" color="primary" />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Loading drawing...
                </span>
              </div>
            </div>
          ) : currentPageId ? (
            <Suspense
              fallback={
                <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-base)' }}>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading viewer...</span>
                </div>
              }
            >
              <TileViewer
                pageId={currentPageId}
                markups={pageMarkups}
                projectId={projectId}
                knowledgeItemId={currentDrawingId || ''}
                onCreateMarkup={handleCreateMarkup}
                onMapReady={setOlMap}
              />
            </Suspense>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-base)' }}>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                No pages available
              </span>
            </div>
          )}

          {/* Drawing toolbar */}
          <DrawingToolbar activeTool={activeTool} onToolChange={setActiveTool} />

          {/* Zoom controls overlay */}
          <ZoomControls map={olMap} />

          {/* Scale calibration info */}
          {currentPageId && tileInfo && (
            <div className="absolute bottom-4 right-4 z-10">
              <ScaleCalibration
                pageId={currentPageId}
                currentScale={tileInfo.scalePixelsPerUnit}
                currentUnit={tileInfo.scaleUnit || 'ft'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Right: Review Panel (35%) */}
      <div
        className="flex flex-col overflow-y-auto"
        style={{
          width: isTablet ? '100%' : '35%', height: isTablet ? '40%' : undefined, minWidth: 0,
        }}
      >
        <ReviewPanel
          projectId={projectId}
          markups={markups}
          currentPageId={currentPageId}
          pages={pages}
          onNavigateToPage={(pageId) => {
            const idx = pages.findIndex((p) => p.id === pageId);
            if (idx >= 0) handlePageChange(idx);
          }}
          revision={null}
        />
      </div>
    </div>
  );
}
